import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Grid } from "antd";
import { SearchOutlined, RightOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import "./filterBar.css";
import "./SearchCardMobile.css";
import { fetchVehicleVersions, fetchVehicleAnalysis } from "../services/api";

const { useBreakpoint } = Grid;

function getSearchParamsFromUrl(search) {
  const params = new URLSearchParams(search || "");
  return {
    vin: params.get("vin") || "",
    mileage: params.get("mileage") || "",
    version: params.get("version") || null,
  };
}

function buildSearchUrl(pathname, { vin, mileage, version }) {
  const params = new URLSearchParams();
  if (vin) params.set("vin", vin);
  if (mileage) params.set("mileage", mileage);
  if (version) params.set("version", version);
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export default function FilterBar({ onDataReceived, onLoadingChange, vehicleData, onChangeVehicle }) {
  const location = useLocation();
  const history = useHistory();
  const [vin, setVin] = useState("");
  const [mileage, setMileage] = useState("");
  const [version, setVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState({ year: null, make: null, model: null, trim: null });
  const [cardCollapsed, setCardCollapsed] = useState(false);

  // When vehicleData is set (mobile vehicle card), sync VIN so versions can be fetched
  useEffect(() => {
    const dataVin = vehicleData?.vehicle_info?.vin ?? vehicleData?.search_metadata?.vin;
    if (dataVin && String(dataVin).trim().length === 17) {
      setVin(String(dataVin).trim().toUpperCase());
      const dataVersion = vehicleData?.search_metadata?.version ?? vehicleData?.vehicle_info?.version;
      if (dataVersion != null) setVersion(dataVersion);
    }
  }, [vehicleData?.vehicle_info?.vin, vehicleData?.search_metadata?.vin, vehicleData?.search_metadata?.version, vehicleData?.vehicle_info?.version]);

  // Initialize from URL on mount and when URL params change (no search is run until user clicks Search)
  useEffect(() => {
    const urlParams = getSearchParamsFromUrl(location.search);
    if (urlParams.vin) {
      setVin(urlParams.vin);
      setMileage(urlParams.mileage || "");
      setVersion(urlParams.version || null);
      setError(null);
    } else if (!vehicleData) {
      localStorage.removeItem("hooptie_vehicle_data");
      setVin("");
      setVersion(null);
      setVersions([]);
      setMileage("");
      setVehicleInfo({ year: null, make: null, model: null, trim: null });
      setError(null);
    }
  }, [location.search, vehicleData]);

  const validateVIN = (vinNumber) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vinNumber.toUpperCase());

  // Fetch versions when VIN is valid (17 characters)
  useEffect(() => {
    const fetchVersions = async () => {
      if (vin && vin.length === 17 && validateVIN(vin)) {
        setLoadingVersions(true);
        // Don't clear version when it came from URL (so search with ?version=... works)
        const urlVersion = getSearchParamsFromUrl(location.search).version;
        if (!urlVersion) {
          setVersion(null); // Reset version when VIN changes (user typed new VIN)
        }
        setVehicleInfo({ year: null, make: null, model: null, trim: null }); // Reset vehicle info
        try {
          const response = await fetchVehicleVersions(vin);
          let versionsData = [];

          if (response?.data) {
            // Extract vehicle_details (year, make, model, trim) from response
            let vehicleDetails = null;

            if (response.data.vehicle_details) {
              vehicleDetails = response.data.vehicle_details;
            } else if (response.data.vehicle_info) {
              vehicleDetails = response.data.vehicle_info;
            } else if (response.data.year || response.data.make || response.data.model) {
              vehicleDetails = response.data;
            }

            if (vehicleDetails) {
              setVehicleInfo({
                year: vehicleDetails.year || null,
                make: vehicleDetails.make || null,
                model: vehicleDetails.model || null,
                trim: vehicleDetails.trim || null,
              });
            }

            // Extract version items - try multiple possible response structures
            if (response.data.versions?.facets?.version && Array.isArray(response.data.versions.facets.version)) {
              versionsData = response.data.versions.facets.version.map(facet => facet.item);
            } else if (response.data.versions && Array.isArray(response.data.versions)) {
              versionsData = response.data.versions;
            } else if (response.data.data && Array.isArray(response.data.data)) {
              versionsData = response.data.data;
            } else if (Array.isArray(response.data)) {
              versionsData = response.data;
            } else if (typeof response.data === 'string') {
              versionsData = [response.data];
            }
          }

          const versionsList = Array.isArray(versionsData) ? versionsData : [];
          setVersions(versionsList);
          // If we had version from URL, match to API's exact string (e.g. casing) so search accepts it
          if (urlVersion && versionsList.length > 0) {
            const normalized = (v) => String(v || "").trim().toLowerCase();
            const match = versionsList.find((v) => normalized(v) === normalized(urlVersion));
            if (match !== undefined) {
              setVersion(match);
            }
          }
        } catch (err) {
          console.error('Error fetching vehicle versions:', err);
          setVersions([]);
          setError(err.response?.data?.message || 'Error fetching vehicle versions. Please try again.');
        } finally {
          setLoadingVersions(false);
        }
      } else {
        setVersions([]);
        if (!getSearchParamsFromUrl(location.search).version) {
          setVersion(null);
        }
        setVehicleInfo({ year: null, make: null, model: null, trim: null });
      }
    };

    fetchVersions();
  }, [vin]);

  const handleSearch = useCallback(async () => {
    if (!vin.trim()) {
      setError('Please enter a VIN number');
      return;
    }
    const cleanVIN = vin.trim().toUpperCase();
    if (!validateVIN(cleanVIN)) {
      setError('Please enter a valid 17-character VIN (no I, O, or Q allowed)');
      return;
    }

    if (!version) {
      setError('Please select a version');
      return;
    }

    if (!mileage || !mileage.trim()) {
      setError('Please enter the vehicle mileage');
      return;
    }

    const numericMileage = mileage.replace(/,/g, '');
    if (isNaN(parseInt(numericMileage, 10)) || parseInt(numericMileage, 10) <= 0) {
      setError('Please enter a valid mileage number');
      return;
    }

    if (!vehicleInfo.make || !vehicleInfo.model || !vehicleInfo.year) {
      setError('Vehicle information is incomplete. Please wait for version data to load.');
      return;
    }

    setLoading(true);
    setError(null);
    onLoadingChange?.(true);

    try {
      // Build payload matching the API requirements
      // numericMileage is already validated above
      const params = {
        make: vehicleInfo.make,
        mileage: parseInt(numericMileage, 10),
        model: vehicleInfo.model,
        radius: 100,
        trim: vehicleInfo.trim || null,
        version: version,
        vin: cleanVIN,
        year: vehicleInfo.year,
      };

      // Remove null values from payload
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchVehicleAnalysis(params);
      console.log('FilterBar: API Response', response);
      console.log('FilterBar: Response status', response?.status);
      console.log('FilterBar: Response data', response?.data);

      // Handle different response structures
      let vehicleData = null;
      if (response?.data?.data) {
        // Response structure: { data: { data: {...} } }
        vehicleData = response.data.data;
      } else if (response?.data) {
        // Response structure: { data: {...} }
        vehicleData = response.data;
      }

      if (vehicleData) {
        console.log('FilterBar: Setting vehicle data', vehicleData);
        localStorage.setItem('hooptie_vehicle_data', JSON.stringify(vehicleData));
        onDataReceived?.(vehicleData);
        // Update URL with search params for shareable / watchlist / recently searched links
        const newUrl = buildSearchUrl(location.pathname, {
          vin: cleanVIN,
          mileage: numericMileage || "",
          version: version || "",
        });
        if (location.pathname + location.search !== newUrl) {
          history.replace(newUrl);
        }
      } else {
        console.error('FilterBar: No data in response', response);
        setError('No data found for this VIN');
      }
    } catch (err) {
      console.error('Error fetching vehicle analysis:', err);
      setError(err.response?.data?.message || err.message || 'Error fetching vehicle analysis. Please try again.');
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [vin, mileage, version, vehicleInfo, onDataReceived, onLoadingChange, location.pathname, history]);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const errorBlock = error ? (
    <div className="filter-bar-error" style={{ color: '#ef4444', fontSize: '12px', marginBottom: '12px', padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px' }}>
      {error}
    </div>
  ) : null;

  if (isMobile) {
    // When vehicle already searched: show compact card with year make model trim + version (read-only), no dropdown
    if (vehicleData && onChangeVehicle) {
      const title = [
        vehicleData?.vehicle_info?.year,
        vehicleData?.vehicle_info?.make,
        vehicleData?.vehicle_info?.model,
        vehicleData?.vehicle_info?.trim,
      ]
        .filter(Boolean)
        .join(" ");
      const versionLabel = version || "—";
      return (
        <div className="msc-wrapper msc-wrapper--compact">
          <div className="msc-card msc-card--vehicle-summary msc-card--vehicle-summary-compact">
            <div className="msc-vehicle-header">
              <div className="msc-vehicle-title-wrap">
                <span className="msc-vehicle-title">{title}</span>
                <span className="msc-vehicle-version-text">Version: {versionLabel}</span>
              </div>
              <button type="button" className="msc-change-btn" onClick={onChangeVehicle}>
                Change
              </button>
            </div>
          </div>
        </div>
      );
    }
    // No vehicle searched: show search form card
    return (
      <div className="msc-wrapper">
        <div className={`msc-card msc-card--search ${cardCollapsed ? "msc-card--collapsed" : ""}`}>
          <button
            type="button"
            className="msc-toggle"
            onClick={() => setCardCollapsed((c) => !c)}
            aria-expanded={!cardCollapsed}
            aria-label={cardCollapsed ? "Show search form" : "Hide search form"}
          >
            <SearchOutlined className="msc-toggle-icon" />
            <span className="msc-toggle-label">Search</span>
            {cardCollapsed ? (
              <DownOutlined className="msc-toggle-arrow" />
            ) : (
              <UpOutlined className="msc-toggle-arrow" />
            )}
          </button>
          {!cardCollapsed && (
            <div className="msc-card-body">
              {errorBlock}
              <div className="msc-fields">
                <div className="msc-field">
                  <label className="msc-label">VIN</label>
                  <div className="msc-input-wrap msc-input-vin">
                    <span className="msc-vin-pill">VIN</span>
                    <input
                      type="text"
                      className="msc-input"
                      placeholder="Enter 17-character VIN"
                      value={vin}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
                        if (value.length <= 17) {
                          setVin(value);
                          setError(null);
                        }
                      }}
                      maxLength={17}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div className="msc-field">
                  <label className="msc-label">VERSION</label>
                  <div className="msc-input-wrap msc-input-version">
                    <select
                      className="msc-select"
                      value={version || ""}
                      onChange={(e) => {
                        setVersion(e.target.value || null);
                        setError(null);
                      }}
                      disabled={!vin || vin.length !== 17 || loadingVersions || loading}
                    >
                      <option value="">{loadingVersions ? "Loading versions..." : "Select Version"}</option>
                      {versions.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                    <DownOutlined className="msc-version-chevron" />
                  </div>
                </div>
                <div className="msc-field">
                  <label className="msc-label">MILEAGE</label>
                  <div className="msc-input-wrap msc-input-mileage">
                    <SearchOutlined className="msc-mileage-icon" />
                    <input
                      type="text"
                      className="msc-input"
                      placeholder="Enter Mileage"
                      value={mileage ? parseInt(mileage.replace(/,/g, '')).toLocaleString() : ''}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        setMileage(numericValue);
                        setError(null);
                      }}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="msc-search-btn"
                onClick={handleSearch}
                disabled={loading || loadingVersions}
              >
                <SearchOutlined className="msc-search-btn-icon" />
                <span>{loading ? "Searching..." : "Search"}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="filter-bar">
      {errorBlock}
      <div className="filter-input-group">
        <label className="filter-label">VIN</label>
        <input
          type="text"
          className="filter-input"
          placeholder="Enter 17-character VIN"
          value={vin}
          onChange={(e) => {
            const value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
            if (value.length <= 17) {
              setVin(value);
              setError(null);
            }
          }}
          maxLength={17}
          disabled={loading}
          style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
        />
      </div>
      <div className="filter-input-group">
        <label className="filter-label">Version</label>
        <select
          className="filter-input filter-select"
          value={version || ""}
          onChange={(e) => {
            setVersion(e.target.value || null);
            setError(null);
          }}
          disabled={!vin || vin.length !== 17 || loadingVersions || loading}
        >
          <option value="">{loadingVersions ? "Loading versions..." : "Select Version"}</option>
          {versions.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>
      <div className="filter-input-group">
        <label className="filter-label">Mileage</label>
        <input
          type="text"
          className="filter-input"
          placeholder="Enter Mileage"
          value={mileage ? parseInt(mileage.replace(/,/g, '')).toLocaleString() : ''}
          onChange={(e) => {
            const numericValue = e.target.value.replace(/[^0-9]/g, '');
            setMileage(numericValue);
            setError(null);
          }}
          disabled={loading}
        />
      </div>
      <button
        className="filter-search-button"
        onClick={handleSearch}
        disabled={loading || loadingVersions}
      >
        {loading ? (
          <span>Searching...</span>
        ) : (
          <>
            <span className="filter-search-icon">🔍</span>
            <span>Search</span>
          </>
        )}
      </button>
    </div>
  );
}
